import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { SelectInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { ApiError } from "@/lib/apiClient";
import {
  buildEmployeeCostContexts,
  calcForEmployee,
  type CalcMode,
} from "@/lib/personnelCostCalc";
import {
  formatEuro,
  formatEuroPerHour,
  settingsForLogin,
  upsertPersonnelCostEmployeeSettings,
  type PersonnelCostCategory,
  type PersonnelCostEmployeeSettings,
  type PersonnelCostEntry,
} from "@/lib/personnelCosts";
import { toast } from "@/lib/toast";
import type { EmployeeOption } from "@/pages/personal/personalkosten/PersonalkostenPage";
import { CostWaterfall } from "@/pages/personal/personalkosten/CostWaterfall";
import {
  formatAmountInput,
  MonthYearAmountFields,
  monthToYear,
  yearToMonth,
} from "@/pages/personal/personalkosten/MonthYearAmountFields";

interface HourlyRateCalculatorProps {
  employees: EmployeeOption[];
  categories: PersonnelCostCategory[];
  entries: PersonnelCostEntry[];
  settings: PersonnelCostEmployeeSettings[];
  selectedLogin: string | null;
  onSelectedLoginChange: (login: string) => void;
  onSettingsSaved: (settings: PersonnelCostEmployeeSettings) => void;
}

export function HourlyRateCalculator({
  employees,
  categories,
  entries,
  settings,
  selectedLogin,
  onSelectedLoginChange,
  onSettingsSaved,
}: HourlyRateCalculatorProps) {
  const [mode, setMode] = useState<CalcMode>("forward");
  const [grossMonthly, setGrossMonthly] = useState(0);
  const [grossYearly, setGrossYearly] = useState(0);
  const [weeklyHours, setWeeklyHours] = useState(40);
  const [vacationDays, setVacationDays] = useState(30);
  const [bridgeHolidaysDays, setBridgeHolidaysDays] = useState(10);
  const [targetHourlyRate, setTargetHourlyRate] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const storedSettings = useMemo(() => {
    if (!selectedLogin) return null;
    return settingsForLogin(settings, selectedLogin);
  }, [settings, selectedLogin]);

  useEffect(() => {
    if (!storedSettings) return;
    const monthly = Number(storedSettings.gross_monthly) || 0;
    setGrossMonthly(monthly);
    setGrossYearly(monthToYear(monthly));
    setWeeklyHours(Number(storedSettings.weekly_hours) || 40);
    setVacationDays(Number(storedSettings.vacation_days) || 30);
    setBridgeHolidaysDays(Number(storedSettings.bridge_holidays_days) || 10);
    setTargetHourlyRate(Number(storedSettings.target_hourly_rate) || 0);
  }, [storedSettings]);

  const calcSettings: PersonnelCostEmployeeSettings = useMemo(() => {
    const login = selectedLogin ?? "";
    return {
      user_login: login,
      gross_monthly: mode === "reverse" ? 0 : grossMonthly,
      weekly_hours: weeklyHours,
      vacation_days: vacationDays,
      bridge_holidays_days: bridgeHolidaysDays,
      target_hourly_rate: targetHourlyRate || null,
      department: storedSettings?.department ?? null,
      updated_at: null,
      updated_by_login: null,
    };
  }, [
    selectedLogin,
    grossMonthly,
    weeklyHours,
    vacationDays,
    bridgeHolidaysDays,
    targetHourlyRate,
    mode,
    storedSettings?.department,
  ]);

  const employeeContexts = useMemo(
    () => buildEmployeeCostContexts(employees, settings),
    [employees, settings],
  );

  const result = useMemo(() => {
    if (!selectedLogin) return null;
    return calcForEmployee({
      login: selectedLogin,
      settings: calcSettings,
      categories,
      allEntries: entries,
      employees: employeeContexts,
      mode,
      targetHourlyRate: mode === "reverse" ? targetHourlyRate : undefined,
    });
  }, [
    selectedLogin,
    calcSettings,
    categories,
    entries,
    employeeContexts,
    mode,
    targetHourlyRate,
  ]);

  const displayHourlyRate = mode === "reverse" ? targetHourlyRate : (result?.hourlyRate ?? 0);

  function setGrossFromMonthly(monthly: number) {
    setGrossMonthly(monthly);
    setGrossYearly(monthToYear(monthly));
  }

  function setGrossFromYearly(yearly: number) {
    setGrossYearly(yearly);
    setGrossMonthly(yearToMonth(yearly));
  }

  async function handleSave() {
    if (!selectedLogin) {
      setError("Bitte einen Mitarbeiter auswählen.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const savedGross =
        mode === "reverse" ? (result?.requiredGrossMonthly ?? grossMonthly) : grossMonthly;
      const saved = await upsertPersonnelCostEmployeeSettings(selectedLogin, {
        gross_monthly: savedGross,
        weekly_hours: weeklyHours,
        vacation_days: vacationDays,
        bridge_holidays_days: bridgeHolidaysDays,
        target_hourly_rate: targetHourlyRate || null,
        department: storedSettings?.department ?? null,
      });
      if (mode === "reverse" && result?.requiredGrossMonthly != null) {
        setGrossFromMonthly(result.requiredGrossMonthly);
      } else {
        setGrossFromMonthly(savedGross);
      }
      onSettingsSaved(saved);
      toast.success("Einstellungen gespeichert.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  if (employees.length === 0) {
    return (
      <Card title="Stundenlohn-Rechner">
        <p className="text-sm text-label-secondary">Keine Mitarbeiter verfügbar.</p>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card title="Eingaben">
        <div className="space-y-4">
          <SelectInput
            label="Mitarbeiter"
            value={selectedLogin ?? ""}
            onChange={(e) => onSelectedLoginChange(e.target.value)}
          >
            <option value="">Bitte wählen …</option>
            {employees.map((emp) => (
              <option key={emp.login} value={emp.login}>
                {emp.display}
              </option>
            ))}
          </SelectInput>

          <SegmentedControl
            value={mode}
            onChange={setMode}
            name="Rechner-Modus"
            options={[
              { value: "forward", label: "Vorwärts" },
              { value: "reverse", label: "Rückwärts" },
            ]}
          />

          {mode === "forward" ? (
            <MonthYearAmountFields
              monthly={grossMonthly ? String(grossMonthly) : ""}
              yearly={grossYearly ? formatAmountInput(grossYearly) : ""}
              monthlyLabel="Brutto €/Monat"
              yearlyLabel="Brutto €/Jahr"
              onMonthlyChange={(value) => {
                const parsed = Number(value);
                if (value === "") {
                  setGrossFromMonthly(0);
                } else if (Number.isFinite(parsed)) {
                  setGrossFromMonthly(parsed);
                }
              }}
              onYearlyChange={(value) => {
                const parsed = Number(value);
                if (value === "") {
                  setGrossFromYearly(0);
                } else if (Number.isFinite(parsed)) {
                  setGrossFromYearly(parsed);
                }
              }}
            />
          ) : (
            <SliderField
              label="Ziel-Stundenlohn"
              value={targetHourlyRate}
              min={0}
              max={150}
              step={0.5}
              format={formatEuroPerHour}
              onChange={setTargetHourlyRate}
            />
          )}

          <SliderField
            label="Wochenstunden"
            value={weeklyHours}
            min={10}
            max={50}
            step={0.5}
            format={(v) => `${v.toLocaleString("de-DE")} h`}
            onChange={setWeeklyHours}
          />

          <SliderField
            label="Urlaubstage/Jahr"
            value={vacationDays}
            min={0}
            max={40}
            step={1}
            format={(v) => `${v} Tage`}
            onChange={setVacationDays}
          />

          <SliderField
            label="Feiertage + Brückentage"
            value={bridgeHolidaysDays}
            min={0}
            max={20}
            step={1}
            format={(v) => `${v} Tage`}
            onChange={setBridgeHolidaysDays}
          />

          {error ? <Alert variant="error">{error}</Alert> : null}

          <Button type="button" onClick={() => void handleSave()} disabled={saving || !selectedLogin}>
            Einstellungen speichern
          </Button>
        </div>
      </Card>

      <Card title="Ergebnis">
        {result ? (
          <div className="space-y-5">
            <div className="rounded-xl border border-accent/30 bg-accent-soft p-5 text-center">
              <p className="text-sm text-label-secondary">
                {mode === "reverse" ? "Ziel-Stundenlohn" : "Voll belasteter Stundenlohn"}
              </p>
              <p className="mt-1 text-4xl font-bold tabular-nums text-accent">
                {formatEuroPerHour(displayHourlyRate)}
              </p>
              <p className="mt-2 text-sm text-label-secondary">
                {result.productiveHoursYear.toLocaleString("de-DE", { maximumFractionDigits: 0 })} produktive
                Stunden/Jahr
              </p>
            </div>

            {mode === "reverse" && result.requiredGrossMonthly != null ? (
              <div className="rounded-lg border border-separator bg-card-elevated p-4">
                <p className="text-sm text-label-secondary">Erforderliches Bruttogehalt</p>
                <p className="text-2xl font-semibold tabular-nums text-label">
                  {formatEuro(result.requiredGrossMonthly)}
                  <span className="text-base font-normal text-label-secondary"> / Monat</span>
                </p>
                <p className="mt-1 text-sm text-label-secondary">
                  {formatEuro(monthToYear(result.requiredGrossMonthly))} / Jahr
                </p>
              </div>
            ) : null}

            <div>
              <p className="mb-3 text-sm font-medium text-label">Kostenaufschlüsselung (Monat)</p>
              <CostWaterfall lines={result.lines} totalMonthly={result.totalMonthly} />
            </div>
          </div>
        ) : (
          <p className="text-sm text-label-secondary">Bitte einen Mitarbeiter auswählen.</p>
        )}
      </Card>
    </div>
  );
}

function SliderField({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <label className="eh-label">{label}</label>
        <span className="text-sm font-medium tabular-nums text-label">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-accent"
      />
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="eh-input"
      />
    </div>
  );
}
